import _extends from "@babel/runtime/helpers/esm/extends";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ModifiersUtils } from 'react-day-picker';
import classNames from 'classnames/bind';
import { arrayOf, bool, func, instanceOf, number, object, oneOf, oneOfType, shape, string } from 'prop-types';
import Datepicker, { disabledDaysPropType, locales, themes } from "../../../Datepicker";
import RangeField from "../../../RangeField";
import UnderlinedField from "../../../UnderlinedField";
import { useDictionary, useMediaQuery } from "../../../hooks";
import { formatDateString, toDate, validateDateString } from "../../../utils/date";
import { getMethodsForTestId } from "../../../utils/getMethodsForTestId";
import TRANSLATIONS from './translations';
import styles from './DateRangePicker.css';
var cx = classNames.bind(styles);
var MASK = '99.99.9999';
var INITIAL_VALUES = ['', ''];

var getStringDate = function getStringDate() {
  return new Date().toLocaleDateString('ru-RU');
};

var formatRange = function formatRange(range) {
  return range.map(formatDateString);
};

var parseDateString = function parseDateString(part) {
  return validateDateString(part) ? part : '';
};

var parseRange = function parseRange(range) {
  return range.map(parseDateString);
};

var compareRanges = function compareRanges(first, second) {
  return !first || !first.every || first.every(function (dateString, i) {
    return second[i] === dateString;
  });
};

var DateRangePicker = function DateRangePicker(_ref) {
  var desktopLeftLabel = _ref.desktopLeftLabel,
      desktopLeftWidth = _ref.desktopLeftWidth,
      desktopRightLabel = _ref.desktopRightLabel,
      disabledDays = _ref.disabledDays,
      leftLabel = _ref.leftLabel,
      locale = _ref.locale,
      mobileLeftLabel = _ref.mobileLeftLabel,
      mobileRightLabel = _ref.mobileRightLabel,
      month = _ref.month,
      monthSelectRef = _ref.monthSelectRef,
      onChange = _ref.onChange,
      onChangeMonth = _ref.onChangeMonth,
      rightLabel = _ref.rightLabel,
      theme = _ref.theme,
      translations = _ref.translations,
      value = _ref.value,
      hideDisabledYears = _ref.hideDisabledYears,
      namespace = _ref.namespace,
      yearSelectRef = _ref.yearSelectRef;
  var breakpoints = useMediaQuery('smMin');
  var isDesktop = breakpoints.smMin;
  var today = useMemo(getStringDate, []);
  var dictionary = useDictionary({
    translations: _objectSpread(_objectSpread({}, TRANSLATIONS), translations),
    locale: locale
  });

  var _useState = useState(INITIAL_VALUES),
      _useState2 = _slicedToArray(_useState, 2),
      values = _useState2[0],
      setValues = _useState2[1];

  var _useState3 = useState(INITIAL_VALUES),
      _useState4 = _slicedToArray(_useState3, 2),
      inputValues = _useState4[0],
      setInputValues = _useState4[1];

  var _useState5 = useState(true),
      _useState6 = _slicedToArray(_useState5, 2),
      isValidInput = _useState6[0],
      setValidInput = _useState6[1];

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'dateRangePicker'),
      getTestId = _getMethodsForTestId.getTestId,
      getNamespace = _getMethodsForTestId.getNamespace;

  useEffect(function () {
    setValidInput(true);

    if (!compareRanges(value, values)) {
      if (value) {
        setValues(parseRange(value));
        setInputValues(formatRange(value));
      } else {
        setValues(INITIAL_VALUES);
        setInputValues(INITIAL_VALUES);
      }
    }
  }, [value, values]);
  var handleChange = useCallback(function (newValue) {
    var newValues = newValue.length ? parseRange(newValue) : INITIAL_VALUES;
    var newInputValue = newValue.length ? formatRange(newValue) : INITIAL_VALUES;
    setValidInput(true);

    if (!compareRanges(newInputValue, inputValues)) {
      setInputValues(newInputValue);
    }

    if (!compareRanges(newValues, values)) {
      setValues(newValues);

      if (onChange) {
        onChange(newValues);
      }
    }
  }, [inputValues, onChange, values]);
  var checkInputDateOfDisabled = useCallback(function (val) {
    var from = toDate(val[0], true);
    var to = parseDateString(val[1]) ? toDate(val[1], true) : null;
    var hasDisabledDays = false;

    if (to) {
      disabledDays === null || disabledDays === void 0 ? void 0 : disabledDays.forEach(function (date) {
        if (date instanceof Date && ModifiersUtils.dayMatchesModifier(date, {
          from: from,
          to: to
        })) {
          hasDisabledDays = true;
        }
      });
    }

    return !ModifiersUtils.dayMatchesModifier(from, disabledDays) && (!to || !ModifiersUtils.dayMatchesModifier(to, disabledDays)) && !hasDisabledDays;
  }, [disabledDays]);
  var handleInputChangeOnDesktop = useCallback(function (newInputValues) {
    var newInputValue = formatRange(newInputValues);
    var newValue = parseRange(newInputValue);
    var isValidValue = checkInputDateOfDisabled(newInputValue);
    setValidInput(isValidValue);

    if (!compareRanges(newInputValue, inputValues)) {
      setInputValues(newInputValue);
    }

    if (isValidValue) {
      if (!compareRanges(newValue, values)) {
        setValues(newValue);

        if (onChange) {
          onChange(newValue);
        }
      }
    }
  }, [onChange, values, inputValues, checkInputDateOfDisabled]);
  var handleInputChangeOnMobile = useCallback(function (event) {
    var _event$target = event.target,
        order = _event$target.dataset.order,
        inputValue = _event$target.value;

    var newValues = _toConsumableArray(values);

    var newInputValues = _toConsumableArray(inputValues);

    newInputValues[order] = formatDateString(inputValue);
    newValues[order] = parseDateString(newInputValues[order]);
    var isValidValue = checkInputDateOfDisabled(newInputValues);
    setValidInput(isValidValue);

    if (!compareRanges(newInputValues, inputValues)) {
      setInputValues(newInputValues);
    }

    if (isValidValue) {
      if (!compareRanges(newValues, values)) {
        setValues(newValues);

        if (onChange) {
          onChange(newValues);
        }
      }
    }
  }, [values, inputValues, checkInputDateOfDisabled, onChange]);
  var leftProps = useMemo(function () {
    var label = desktopLeftLabel || leftLabel || dictionary.t('start');
    return {
      label: label,
      mask: MASK,
      maskChar: ' ',
      style: {
        width: desktopLeftWidth
      }
    };
  }, [desktopLeftLabel, desktopLeftWidth, dictionary, leftLabel]);
  var rightProps = useMemo(function () {
    var label = desktopRightLabel || rightLabel || dictionary.t('endDate');
    return {
      label: label,
      mask: MASK,
      maskChar: ' '
    };
  }, [desktopRightLabel, dictionary, rightLabel]);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cx('DateRangePicker')
  }, getTestId()), /*#__PURE__*/React.createElement("div", {
    className: cx('Value')
  }, isDesktop ? /*#__PURE__*/React.createElement(RangeField, {
    invalid: !isValidInput,
    leftProps: leftProps,
    rightProps: rightProps,
    onChange: handleInputChangeOnDesktop,
    theme: theme,
    value: inputValues,
    namespace: getNamespace()
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(UnderlinedField, {
    "data-order": "0",
    invalid: !isValidInput,
    label: mobileLeftLabel || leftLabel || dictionary.t('start'),
    mask: MASK,
    maskChar: null,
    onChange: handleInputChangeOnMobile,
    onFocus: true,
    placeholder: today,
    theme: theme,
    value: inputValues[0],
    weight: "bold",
    namespace: getNamespace('startDate')
  }), /*#__PURE__*/React.createElement(UnderlinedField, {
    "data-order": "1",
    invalid: !isValidInput,
    label: mobileRightLabel || rightLabel || dictionary.t('end'),
    mask: MASK,
    maskChar: null,
    onChange: handleInputChangeOnMobile,
    placeholder: today,
    theme: theme,
    value: inputValues[1],
    weight: "bold",
    namespace: getNamespace('endDate')
  }))), /*#__PURE__*/React.createElement("div", {
    className: cx('datepicker')
  }, /*#__PURE__*/React.createElement(Datepicker, {
    block: true,
    dateRangePicker: true,
    disabledDays: disabledDays,
    downcaseMonth: false,
    hideDisabledYears: hideDisabledYears,
    locale: locale,
    month: month,
    monthSelectRef: monthSelectRef,
    navbarType: isDesktop ? 'unbordered' : 'date',
    onChange: handleChange,
    onChangeMonth: onChangeMonth,
    selectable: true,
    theme: theme,
    value: values.join(' - '),
    withRange: true,
    yearSelectRef: yearSelectRef
  })));
};

DateRangePicker.propTypes = process.env.NODE_ENV !== "production" ? {
  desktopLeftWidth: oneOfType([number, string]),
  desktopLeftLabel: string,
  desktopRightLabel: string,
  disabledDays: disabledDaysPropType,
  mobileLeftLabel: string,
  mobileRightLabel: string,
  leftLabel: string,
  rightLabel: string,
  locale: oneOf(locales),
  month: instanceOf(Date),
  monthSelectRef: object,
  onChange: func,
  onChangeMonth: func,
  theme: oneOf(themes),
  translations: shape({
    end: shape({
      ru: string,
      en: string
    }),
    endDate: shape({
      ru: string,
      en: string
    }),
    start: shape({
      ru: string,
      en: string
    })
  }),
  value: arrayOf(string),
  hideDisabledYears: bool,
  namespace: string,
  yearSelectRef: object
} : {};
DateRangePicker.defaultProps = {
  locale: 'ru',
  theme: 'b2c',
  translations: {}
};
export { locales, themes };
export default DateRangePicker;